import {computed, Injectable, signal} from '@angular/core';
import { AccordionItemComponent } from './accordion-item/accordion-item.component';

@Injectable({
  providedIn: 'root'
})
export class AccordionService {

  private readonly openItemsSet = signal<Set<AccordionItemComponent>>(new Set());

  private readonly allItems = signal<AccordionItemComponent[]>([]);

  public readonly openItems = this.openItemsSet.asReadonly();
  private readonly openItem = computed(() => {
    const items = this.openItems();
    return (items.values().next().value as AccordionItemComponent) ?? null;
  });
  private readonly openIndex = computed(() => {
    const openItem = this.openItem();
    return openItem ? this.allItems().indexOf(openItem) : -1;
  });
  private readonly multiple = signal(false);
  private readonly allowUserInteractionSignal = signal(false);
  public readonly allowUserInteraction = this.allowUserInteractionSignal.asReadonly();

  constructor() { }

  public setMultiple(multiple: boolean): void {
    this.multiple.set(multiple);
  }

  public setAllowUserInteraction(allow: boolean): void {
    this.allowUserInteractionSignal.set(allow);
  }

  public isOpen(item: AccordionItemComponent): boolean {
    return this.openItems().has(item);
  }

  public open(item: AccordionItemComponent): void {
    const newOpenItems = new Set(this.openItems());
    if (!this.multiple()) {
      newOpenItems.clear();
    }
    newOpenItems.add(item);
    this.openItemsSet.set(newOpenItems);
  }

  public close(item: AccordionItemComponent): void {
    if (this.openItems().has(item)) {
      const newOpenItems = new Set(this.openItems());
      newOpenItems.delete(item);
      this.openItemsSet.set(newOpenItems);
    }
  }

  public openPrevious(): void {
    const openIndex = this.openIndex();
    const allItems = this.allItems();
    if (openIndex > 0) {
      this.open(allItems[openIndex - 1]);
    }
  }

  public openNext(): void {
    const openIndex = this.openIndex();
    const allItems = this.allItems();
    if (openIndex < allItems.length - 1) {
      this.open(allItems[openIndex + 1]);
    }
  }

  public setState(item: AccordionItemComponent, mustBeOpen: boolean): void {
    if (mustBeOpen) {
      this.open(item);
    } else {
      this.close(item);
    }
  }

  public toggle(item: AccordionItemComponent): void {
    if (this.isOpen(item)) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  public registerItem(item: AccordionItemComponent): void {
    this.allItems.update(items => [...items, item]);
  }

  public unregisterItem(item: AccordionItemComponent): void {
    this.allItems.update(items => items.filter(i => i !== item));
  }
}
